import {
  RolePayload,
  RolesPaginatedPayload,
  RolesService,
} from "../services/roles.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUserStore } from "./useUserStore";
import {
  RoleTypeEnum,
  StandardAccountRoleSubtypeEnum,
} from "@/domain/roles.enum";

export const useRoles = () => {
  const { userInfo } = useUserStore();
  const userRole = userInfo?.roles[0] as unknown as string;

  const { data: roles, isLoading: isLoadingRoles } = useQuery({
    queryKey: ["roles"],
    queryFn: () => RolesService.findAll({ status: "ACTIVE" }),
    select: (data) => {
      if (userRole) {
        const isSysadmin = data.some(
          (role) => role.name === "Sysadmin" && role._id === userRole
        );
        const isSupportOrAccountAdmin = data.some(
          (role) =>
            (role.name === "Support" || role.name === "Account Admin") &&
            role._id === userRole
        );

        // Sysadmin, Support and Account Admin can assign any role (including
        // Sysadmin). Other roles only see STANDARD_SYSTEM_ROLE entries.
        if (!isSysadmin && !isSupportOrAccountAdmin) {
          data = data.filter((role) => role.type === "STANDARD_SYSTEM_ROLE");
        }
      }
      return data;
    },
  });

  return {
    roles,
    isLoadingRoles,
  };
};

export const useOfficerRoles = () => {
  const { data: officerRoles, isLoading: isLoadingOfficerRoles } = useQuery({
    queryKey: ["officerRoles"],
    queryFn: () => RolesService.findAll({ status: "ACTIVE" }),
    select: (data) => {
      return data.filter(
        (role) =>
          role.type === RoleTypeEnum.STANDARD_ACCOUNT_ROLE &&
          role.subType === StandardAccountRoleSubtypeEnum.OFFICER
      );
    },
  });

  return {
    officerRoles,
    isLoadingOfficerRoles,
  };
};

export const useRolesPaginated = (filters: RolesPaginatedPayload) => {
  const {
    data: rolesPaginated,
    isLoading: isLoadingRolesPaginated,
    isFetching: isFetchingRolesPaginated,
    refetch: refetchRolesPaginated,
  } = useQuery({
    queryKey: ["pret-roles-paginated", filters],
    queryFn: () => RolesService.findAllPaginated(filters),
  });

  return {
    rolesPaginated,
    isLoadingRolesPaginated,
    isFetchingRolesPaginated,
    refetchRolesPaginated,
  };
};

export const useRoleById = (id: string, enabled = true) => {
  const {
    data: role,
    isLoading: isLoadingRole,
    refetch: refetchRole,
  } = useQuery({
    queryKey: ["role-by-id", id],
    queryFn: () => RolesService.findById(id),
    enabled: !!id && enabled,
  });

  return {
    role,
    isLoadingRole,
    refetchRole,
  };
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RolePayload) => RolesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pret-roles-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RolePayload }) =>
      RolesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pret-roles-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["role-by-id"] });
    },
  });
};

export const useArchiveRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RolesService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pret-roles-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};
